import { useEffect, useState } from "react";
import { Cpu, Loader2, FileText, Trash2, Plus, Sparkles } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { renderMarkdown } from "@/lib/md";

const Mono = ({ children, className = "" }) => (
  <span className={`font-mono uppercase tracking-[0.2em] ${className}`}>{children}</span>
);

const TYPE_GROUPS = [
  {
    group: "Start here — quick wins",
    types: [
      { key: "capability_statement", label: "Capability Statement" },
      { key: "whs", label: "WHS Management Plan" },
      { key: "swms", label: "Safe Work Method Statement" },
      { key: "quality_policy", label: "Quality Policy (ISO 9001)" },
      { key: "code_of_conduct", label: "Code of Conduct" },
    ],
  },
  {
    group: "Tender & prequalification pack",
    types: [
      { key: "environmental_policy", label: "Environmental Plan (ISO 14001)" },
      { key: "risk_management_plan", label: "Risk Mgmt Plan (ISO 31000)" },
      { key: "equal_opportunity", label: "Equal Opportunity & Diversity" },
      { key: "modern_slavery", label: "Modern Slavery Policy" },
      { key: "drug_alcohol", label: "Drug & Alcohol Policy" },
      { key: "business_continuity", label: "Business Continuity Plan" },
    ],
  },
  {
    group: "Safety & systems",
    types: [
      { key: "swi", label: "Safe Work Instruction" },
      { key: "sop", label: "Standard Operating Procedure" },
      { key: "system", label: "Repeatable Business System" },
      { key: "iso", label: "ISO 9001 Procedure" },
    ],
  },
];

export default function AIAssistant() {
  const [docs, setDocs] = useState([]);
  const [active, setActive] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ doc_type: "capability_statement", title: "", context: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = () => api.get("/ai/documents").then((r) => setDocs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const generate = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await api.post("/ai/generate", form);
      await load();
      setActive(res.data);
      setCreating(false);
      setForm({ doc_type: "capability_statement", title: "", context: "" });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/ai/documents/${id}`);
    if (active?.id === id) setActive(null);
    load();
  };

  return (
    <div className="space-y-8 text-white" data-testid="ai-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <Mono className="text-gold-400 text-[0.65rem]">Sheet 01 — AI Document Builder</Mono>
          <h1 className="font-heading font-light text-4xl tracking-tight mt-3">Generate the documents that win work</h1>
          <p className="text-white/55 mt-3 max-w-xl font-light">
            Start with the quick wins most panels accept — capability statement, WHS plan, SWMS and
            policies. Submission-ready and standards-aligned (final acceptance rests with the governing body).
          </p>
        </div>
        <button
          onClick={() => { setCreating(true); setActive(null); }}
          data-testid="new-generation-btn"
          className="bg-gold-500 text-navy-950 font-semibold px-6 py-3 rounded-sm hover:bg-gold-400 transition-colors flex items-center gap-2"
        >
          <Plus className="h-4 w-4" /> New document
        </button>
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        {/* List */}
        <div className="lg:col-span-4 space-y-3">
          <Mono className="text-white/45 text-[0.55rem]">Your documents ({docs.length})</Mono>
          {docs.length === 0 && (
            <div className="border border-dashed border-gold-500/25 p-8 text-center text-white/45 text-sm font-light">
              No documents yet. Generate your first quick win.
            </div>
          )}
          {docs.map((d) => (
            <div
              key={d.id}
              onClick={() => { setActive(d); setCreating(false); }}
              data-testid={`doc-item-${d.id}`}
              className={`cursor-pointer border p-4 transition-colors ${
                active?.id === d.id ? "border-gold-500 bg-navy-800" : "border-gold-500/20 bg-navy-900/40 hover:border-gold-500/50"
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <FileText className="h-4 w-4 text-gold-400 mt-1 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{d.title}</div>
                  <Mono className="text-white/40 text-[0.5rem] mt-1 block">{d.doc_type.replace(/_/g, " ")}</Mono>
                </div>
                <button onClick={(e) => { e.stopPropagation(); remove(d.id); }} data-testid={`delete-doc-${d.id}`} className="text-white/30 hover:text-red-400">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Viewer / Creator */}
        <div className="lg:col-span-8">
          {creating || (!active && docs.length === 0) ? (
            <form onSubmit={generate} className="border border-gold-500/25 bg-navy-900/40 p-8 space-y-6" data-testid="generate-form">
              {error && <div className="bg-red-500/10 border border-red-500/30 text-red-300 text-sm px-4 py-3">{error}</div>}
              <div>
                <Mono className="text-white/60 text-[0.6rem] block mb-3">Document type</Mono>
                <div className="space-y-4">
                  {TYPE_GROUPS.map((g) => (
                    <div key={g.group}>
                      <Mono className="text-gold-400 text-[0.5rem] mb-2 block">{g.group}</Mono>
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {g.types.map((t) => (
                          <button
                            type="button"
                            key={t.key}
                            onClick={() => setForm((f) => ({ ...f, doc_type: t.key }))}
                            data-testid={`type-${t.key}`}
                            className={`text-left text-sm p-3 border transition-colors ${
                              form.doc_type === t.key ? "border-gold-500 bg-gold-500 text-navy-950 font-semibold" : "border-gold-500/20 text-white/80 hover:border-gold-500/50"
                            }`}
                          >
                            {t.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <Mono className="text-white/60 text-[0.6rem] block mb-2">Title</Mono>
                <input
                  required
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  data-testid="gen-title"
                  placeholder="e.g. Acme Civil — Capability Statement"
                  className="w-full bg-navy-950 border border-gold-500/20 px-4 py-3 text-white focus:outline-none focus:border-gold-500 placeholder:text-white/30"
                />
              </div>
              <div>
                <Mono className="text-white/60 text-[0.6rem] block mb-2">Context / requirements</Mono>
                <textarea
                  required
                  rows={5}
                  value={form.context}
                  onChange={(e) => setForm((f) => ({ ...f, context: e.target.value }))}
                  data-testid="gen-context"
                  placeholder="Describe your firm, services, team and any specifics…"
                  className="w-full bg-navy-950 border border-gold-500/20 px-4 py-3 text-white focus:outline-none focus:border-gold-500 resize-none placeholder:text-white/30"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="generate-submit-btn"
                className="bg-gold-500 text-navy-950 font-semibold px-7 py-3.5 rounded-sm hover:bg-gold-400 transition-colors flex items-center gap-2 disabled:opacity-60"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                {loading ? "Generating…" : "Generate document"}
              </button>
            </form>
          ) : active ? (
            <div className="border border-gold-500/25 bg-navy-900/40" data-testid="doc-viewer">
              <div className="border-b border-gold-500/20 px-6 py-4 flex items-center justify-between">
                <div>
                  <Mono className="text-gold-400 text-[0.55rem]">{active.doc_type.replace(/_/g, " ")}</Mono>
                  <h2 className="font-heading text-xl mt-0.5">{active.title}</h2>
                </div>
                <Cpu className="h-5 w-5 text-gold-400" />
              </div>
              {/* document rendered on a light "sheet" for readability */}
              <div className="p-2">
                <div className="bg-white p-8 md-body" dangerouslySetInnerHTML={{ __html: renderMarkdown(active.content) }} />
              </div>
            </div>
          ) : (
            <div className="border border-dashed border-gold-500/25 p-16 text-center text-white/45">
              <Cpu className="h-10 w-10 text-gold-400 mx-auto mb-4" />
              Select a document or create a new one.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
